import React, { Suspense, useEffect } from 'react'

const ExternalRedirect = ({ to, label }: { to: string; label: string }) => {
  useEffect(() => {
    const t = setTimeout(() => window.location.replace(to), 700)
    return () => clearTimeout(t)
  }, [to])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px' }}>
      <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ fontSize: '16px', color: '#6b7280' }}>Navigating to {label}...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
import ApplicationLayout from '@/layouts/ApplicationLayout'
import {
  Route,
  Routes as ReactRoutes,
  Outlet,
  Navigate,
} from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/use-toast'

// Pages
import Home from '@/pages/home'
import About from '@/pages/about'
// import Destination from '@/pages/destination'
import Regional from '@/pages/regional'
import Schedule from '@/pages/monthlyschedule'
import HelpFaq from '@/pages/help-faq'
import RDCenter from '@/pages/rdc'
import ContactUs from '@/pages/contact-us'
import Cart from '@/pages/cart'
import Signin from '@/pages/signin'
import Signup from '@/pages/signup'
import Subscriptions from '@/pages/profile/subscriptions'
import Admin from '@/pages/profile/admin'
import Settings from '@/pages/profile/settings'
import Password from '@/pages/profile/settings/password'
import PaymentMethods from '@/pages/profile/settings/payment_methods'
import EditUser from '@/pages/profile/settings/edit_user'
import Enroll from '@/pages/profile/enroll'
import ManageUsers from '@/pages/profile/manageUsers'
import EnrollUser from '@/pages/profile/enrollUser'
import Workbooks from '@/pages/profile/workbooks'
import Checkout from '@/pages/checkout'
import ResetPassword from '@/pages/reset-password'
import Invoices from '@/pages/profile/settings/invoices'
import SettingsHome from '@/pages/profile/settings/home'
import LoggedInLayout from '@/layouts/LoggedInLayout'
import NotFound from '@/pages/not-found'
import MeinekeRedirect from '@/components/Redirects/MeinekeRedirect'
import NiadaRedirect from '@/components/Redirects/NiadaRedirect'
import ClassRedirect from '@/components/Redirects/ClassRedirect'
import SkillTracks from '@/pages/skilltracks.js'

// See: https://github.com/vitejs/vite/issues/10460 for why the .js and paths
 const Instructors = React.lazy(() => import('../pages/instructors.js'))
const PdfViewerPage = React.lazy(() => import('@/pages/PdfViewerPage.js'))

const PrivateRoute: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth()

  if (isAuthenticated()) {
    return <>{children ?? <Outlet />}</>
  } else {
    toast({
      title: 'Not signed in',
      description: 'You need to sign in to access that page',
      variant: 'destructive',
    })
    return <Navigate to="/signin" replace />
  }
}

function Routes() {
  return (
    <ReactRoutes>
      <Route element={<ApplicationLayout />}>
        <Route path="/" element={<Home />} />
        <Route
          path="/course-catalog"
          element={
            <Suspense fallback={<div>Loading Catalog...</div>}>
              <PdfViewerPage />
            </Suspense>
          }
        />
        <Route path="/signin" element={<Signin />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/cart" element={<Cart />} />

        {/* Redirect Route */}
        <Route path="/meinekepromo" element={<MeinekeRedirect />} />
        <Route path="/niadapromo" element={<NiadaRedirect />} />
        <Route path="/class" element={<ClassRedirect />} />

        {/* Regional Page */}
        <Route path="/regional" element={<Regional />} />

          {/* UltraS2 Form Menu Page */}
          <Route path="/ultras2" element={<ExternalRedirect to="https://cti.formstack.com/forms/ultra_s2_registration_form" label="Ultra S2 Course Registration" />} />
          <Route path="/ultras2/*" element={<ExternalRedirect to="https://cti.formstack.com/forms/ultra_s2_registration_form" label="Ultra S2 Course Registration" />} />

          {/* Open House Form Menu Page */}
          <Route path="/openhouse" element={<ExternalRedirect to="https://cti.formstack.com/forms/ctirdc_openhouse" label="Open House Registration" />} />
          <Route path="/openhouse/*" element={<ExternalRedirect to="https://cti.formstack.com/forms/ctirdc_openhouse" label="Open House Registration" />} />

        {/* Training Menu */}
        <Route path="/monthlyschedule" element={<Schedule />} />
        {/* <Route path="/destination" element={<Destination />} /> */}
        
        {/* Skills Tracks */}
        {(() => {
          const isSkillTracksEnabled = ['qa.ctionline.com', 'localhost', 'dev.ctionline.com'].includes(window.location.hostname);
          if (isSkillTracksEnabled) {
            return <Route path="/skilltracks" element={<SkillTracks />} />;
          }
        })()}
        
        {/* About Us Menu */}
        <Route path="/about" element={<About />} />
        <Route path="/rdc" element={<RDCenter />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route
          path="/instructors"
          element={
            <Suspense fallback={<div>Loading Instructors...</div>}>
              <Instructors />
            </Suspense>
          }
        />

        {/* Help & Faq */}
        <Route path="/help-faq" element={<HelpFaq />} />
        <Route path="*" element={<NotFound />} />

        {/* Private routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<LoggedInLayout />}>
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/profile">
              <Route path="settings" element={<Settings />}>
                <Route index element={<SettingsHome />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="edituser" element={<EditUser />} />
                <Route path="password" element={<Password />} />
                <Route path="payment-methods" element={<PaymentMethods />} />
                <Route path="invoices" element={<Invoices />} />
              </Route>
              <Route path="admin" element={<Admin />}>
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="enroll" element={<Enroll />} />
                <Route path="manageusers" element={<ManageUsers />} />
                <Route path="enrolluser" element={<EnrollUser />} />
                <Route path="workbooks" element={<Workbooks />} />
                <Route path="password" element={<Password />} />
                <Route path="payment-methods" element={<PaymentMethods />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </ReactRoutes>
  )
}

export default Routes
